import { z } from 'zod';
import { LIMITS } from '../registries/limits.ts';
import { CALL_SOURCES } from '../registries/media.ts';
import { PARTICIPANT_ROLES } from '../registries/roles.ts';
import { DurationMs, EpochMs, Id, IsoDateTime, LanguageCode, pageOf } from './primitives.ts';
import { ScreeningPublic } from './screenings.ts';

export const ParticipantRoleSchema = z.enum(PARTICIPANT_ROLES);
export const CallSourceSchema = z.enum(CALL_SOURCES);

export const PLAYBACK_STATES = ['paused', 'playing'] as const;
export const PlaybackStateName = z.enum(PLAYBACK_STATES);

/**
 * Authoritative shared playback clock. While playing, the expected position at server time `t` is
 * `positionMs + (t - updatedAtMs)` — see `expectedPositionMs`.
 */
export const PlaybackState = z.object({
  state: PlaybackStateName,
  positionMs: DurationMs,
  updatedAtMs: EpochMs,
  /** Monotonic per screening; commands carry the version they were based on (CAS). */
  version: z.number().int().nonnegative(),
  updatedBy: z.object({ identity: z.string(), displayName: z.string() }).nullable(),
});
export type PlaybackState = z.infer<typeof PlaybackState>;

export const PLAYBACK_ACTIONS = ['play', 'pause', 'seek'] as const;
export const PlaybackAction = z.enum(PLAYBACK_ACTIONS);
export type PlaybackAction = z.infer<typeof PlaybackAction>;

/**
 * `play`/`pause` set the state at `positionMs`; `seek` moves the position and keeps the state.
 * `expectedVersion` must equal the current version or the command is rejected with
 * `playback_version_conflict` (details: `{ playback }`).
 */
export const PlaybackCommand = z.object({
  action: PlaybackAction,
  positionMs: DurationMs,
  expectedVersion: z.number().int().nonnegative(),
});
export type PlaybackCommand = z.infer<typeof PlaybackCommand>;

export const ChatAuthor = z.object({
  identity: z.string(),
  displayName: z.string(),
  role: ParticipantRoleSchema,
});

export const ChatMessage = z.object({
  id: Id,
  screeningId: Id,
  clientMessageId: Id,
  author: ChatAuthor,
  body: z.string(),
  createdAt: IsoDateTime,
});
export type ChatMessage = z.infer<typeof ChatMessage>;

/** Page of chat history, items ordered oldest → newest; `nextCursor` loads older messages. */
export const ChatPage = pageOf(ChatMessage);
export type ChatPage = z.infer<typeof ChatPage>;

export const ChatPageQuery = z.object({
  cursor: z.string().min(1).max(200).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(LIMITS.pageSize.min)
    .max(LIMITS.pageSize.max)
    .default(LIMITS.pageSize.default),
});

export const PostChatBody = z.object({
  /** Client-generated id; posting the same id twice returns the original message. */
  clientMessageId: Id,
  body: z.string().trim().min(LIMITS.chatBody.min).max(LIMITS.chatBody.max),
});

export const ManifestTrack = z.object({
  id: Id,
  language: LanguageCode.nullable(),
  label: z.string().nullable(),
  isDefault: z.boolean(),
});

/** Everything the player needs. Audio tracks are listed in the same order as in the master playlist. */
export const MediaManifest = z.object({
  assetId: Id,
  masterUrl: z.string(),
  posterUrl: z.string().nullable(),
  posterAspectRatio: z.number().positive().nullable(),
  durationMs: DurationMs,
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  audioTracks: z.array(ManifestTrack),
  subtitleTracks: z.array(ManifestTrack.extend({ url: z.string() })),
});
export type MediaManifest = z.infer<typeof MediaManifest>;

export const ParticipantCapabilities = z.object({
  controlPlayback: z.boolean(),
  moderate: z.boolean(),
  manageScreening: z.boolean(),
});

export const RoomMe = z.object({
  identity: z.string(),
  displayName: z.string(),
  role: ParticipantRoleSchema,
  capabilities: ParticipantCapabilities,
});
export type RoomMe = z.infer<typeof RoomMe>;

/**
 * Initial room state. Fetching it also refreshes the media grant cookie for the screening's film.
 * `media` is null while the film is not playable.
 */
export const RoomSnapshot = z.object({
  screening: ScreeningPublic,
  media: MediaManifest.nullable(),
  playback: PlaybackState,
  me: RoomMe,
  chat: ChatPage,
  serverTimeMs: EpochMs,
});
export type RoomSnapshot = z.infer<typeof RoomSnapshot>;

export const RtcCredentials = z.object({
  /** LiveKit signalling URL (wss://…). */
  url: z.string(),
  token: z.string(),
});
export type RtcCredentials = z.infer<typeof RtcCredentials>;

export const MuteParticipantBody = z.object({ source: CallSourceSchema });

export const RemoveParticipantBody = z.object({
  /** Also refuse this guest from rejoining the screening. */
  ban: z.boolean(),
});

export const ServerTime = z.object({ serverTimeMs: EpochMs });
